import { Fragment, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useApi, useMutation } from '../lib/useApi.ts';
import { api } from '../lib/api.ts';
import { useSession } from '../lib/session.tsx';
import { money, date, dateTime, titleCase } from '../lib/format.ts';
import { PersonDetail } from '../components/PersonDetail.tsx';
import { PhotoPicker } from '../components/PhotoPicker.tsx';
import { PaymentMethods } from '../components/PaymentMethods.tsx';
import { Receipt } from '../components/Receipt.tsx';
import { UsageReport } from '../components/UsageReport.tsx';
import { useSort, SortTh, SortPicker } from '../lib/useSort.tsx';
import {
  BylawNotices,
  AccountStatusPill,
  AgingBar,
  Card,
  Empty,
  ErrorBanner,
  Loading,
  Meter,
  Money,
  Tile,
} from '../components/ui.tsx';

interface AccountRow {
  member_account_id: string;
  account_number: string;
  display_name: string;
  status: string;
  balance_minor: number;
  bucket_90_plus_minor: number;
  membership_number: string | null;
  category_code: string | null;
  category_name: string | null;
  membership_status: string | null;
  member_since: string | null;
  people_who_may_charge: number;
  /**
   * The club's own fields, keyed by code — whatever it has marked
   * `show_on_roster` (0192). Occupation and employer at RCC; a sailing
   * club would put a boat name here and this file would not change.
   * The PRIMARY member's answers: a roster row is a household, and a
   * column that shows the spouse's job on some rows and the member's on
   * others means something different on every line.
   */
  roster_fields: Record<string, string> | null;
}

interface RosterColumn {
  code: string;
  label: string;
}

interface CategoryRow {
  id: string;
  code: string;
  name: string;
  active_memberships: number;
}

export function MembersPage() {
  const { currency, atLeast } = useSession();

  /**
   * The filters live in the address bar.
   *
   * They were component state, which made this list unreachable: the
   * dashboard's "Accounts not current" tile has a list behind it and no
   * way to point at it, and a member of staff who found the right filter
   * could not send it to anybody or come back to it. In the URL, every
   * tile and every link can name the view it means, and the back button
   * does what it looks like it does.
   *
   * `replace`, so paging through statuses does not build a history stack
   * somebody has to press Back through eleven times to leave the screen.
   */
  const [params, setParams] = useSearchParams();
  const search = params.get('search') ?? '';
  const status = params.get('status') ?? '';
  const category = params.get('category') ?? '';

  const setFilter = (key: string, value: string) =>
    setParams(
      (current) => {
        const next = new URLSearchParams(current);
        if (value) next.set(key, value);
        else next.delete(key);
        return next;
      },
      { replace: true },
    );

  const query = new URLSearchParams();
  if (search) query.set('search', search);
  if (status) query.set('status', status);
  if (category) query.set('category', category);
  query.set('limit', '100');

  const { data, error, loading } = useApi<{
    accounts: AccountRow[];
    columns: RosterColumn[];
    total: number;
  }>(`/v1/member-accounts?${query}`, [query.toString()]);

  const extra = data?.columns ?? [];
  const categories = useApi<{ categories: CategoryRow[] }>('/v1/membership-categories');

  /* The roster is read for a different reason every time it is opened:
     alphabetically to find somebody, by balance to see who is worst, by
     "member since" to write the anniversary list. `paged`, because the
     query above asks for a hundred and a club has more than that — the
     heading says so rather than letting a sorted page pass for a sorted
     roster. */
  const sorted = useSort(
    data?.accounts,
    {
      account: (a) => a.account_number,
      member: (a) => a.display_name,
      category: (a) => a.category_name,
      member_since: (a) => a.member_since,
      status: (a) => a.status,
      over_90: (a) => Number(a.bucket_90_plus_minor),
      balance: (a) => Number(a.balance_minor),
      // The club's own columns sort like any other. Built from the
      // response rather than listed, so a club that adds a roster field
      // gets a sortable column for it and nothing here changes.
      ...Object.fromEntries(
        extra.map((c) => [c.code, (a: AccountRow) => a.roster_fields?.[c.code] ?? null]),
      ),
    },
    { key: 'member', paged: (data?.total ?? 0) > (data?.accounts.length ?? 0) },
  );

  return (
    <>
      <ErrorBanner error={error} />
      <Card
        title="Member Accounts"
        actions={
          <>
            <span className="muted small">
              {data ? `${data.total} account${data.total === 1 ? '' : 's'}` : ''}
            </span>
            {atLeast('manager') && (
              <Link to="/members/new" style={{ marginLeft: 12 }}>
                <button className="gold small">Add a member</button>
              </Link>
            )}
          </>
        }
      >
        <div className="row" style={{ marginBottom: 4 }}>
          <input
            // The placeholder used to say "name or account number", which
            // was both what the search did and the reason it kept failing:
            // it told the desk the only two things to try. It now searches
            // everybody on the membership, and says so.
            placeholder="Name, account or membership number, email or phone"
            value={search}
            onChange={(e) => setFilter('search', e.target.value)}
            style={{ maxWidth: 340 }}
            aria-label="Search members"
          />
          <select
            value={status}
            onChange={(e) => setFilter('status', e.target.value)}
            style={{ maxWidth: 180 }}
            aria-label="Filter by status"
          >
            <option value="">Any status</option>
            <option value="current">Current</option>
            <option value="past_due">Past due</option>
            <option value="suspended">Suspended</option>
            <option value="collections">Collections</option>
            <option value="closed">Closed</option>
          </select>
          <select
            value={category}
            onChange={(e) => setFilter('category', e.target.value)}
            style={{ maxWidth: 200 }}
            aria-label="Filter by category"
          >
            <option value="">Any category</option>
            {(categories.data?.categories ?? []).map((c) => (
              <option key={c.id} value={c.code}>
                {c.name} ({c.active_memberships})
              </option>
            ))}
          </select>
        </div>
      </Card>

      <Card flush>
        {loading ? (
          <div style={{ padding: 16 }}>
            <Loading rows={6} />
          </div>
        ) : !data || data.accounts.length === 0 ? (
          <Empty>No accounts match.</Empty>
        ) : (
          /* `data--cards`: on a phone each row becomes a block instead of
             scrolling sideways. Seven columns is 922px of table in a
             360px window — 562px of it hidden behind a swipe with
             nothing to say it is there, and the two columns that fall
             off are STATUS and BALANCE, which is the entire question
             somebody asks at the bar. `data-label` is what each cell
             calls itself once the header row is gone. */
          <>
          <SortPicker
            s={sorted}
            labels={{
              account: 'Account number',
              member: 'Member',
              category: 'Category',
              member_since: 'Member since',
              status: 'Status',
              over_90: 'Over 90 days',
              balance: 'Balance',
              ...Object.fromEntries(extra.map((c) => [c.code, c.label])),
            }}
          />
          <table className="data data--cards">
            <thead>
              <tr>
                <SortTh s={sorted} col="account">Account</SortTh>
                <SortTh s={sorted} col="member">Member</SortTh>
                <SortTh s={sorted} col="category">Category</SortTh>
                <SortTh s={sorted} col="member_since">Member since</SortTh>
                {extra.map((c) => (
                  <SortTh key={c.code} s={sorted} col={c.code}>
                    {c.label}
                  </SortTh>
                ))}
                <SortTh s={sorted} col="status">Status</SortTh>
                <SortTh s={sorted} col="over_90" className="num">Over 90 days</SortTh>
                <SortTh s={sorted} col="balance" className="num">Balance</SortTh>
              </tr>
            </thead>
            <tbody>
              {sorted.rows.map((a) => (
                <tr key={a.member_account_id}>
                  <td className="mono" data-label="Account">{a.account_number}</td>
                  <td data-label="Member" className="cell--title">
                    <Link to={`/members/${a.member_account_id}`}>{a.display_name}</Link>
                    {/* Its own element rather than a trailing span, so the
                        card layout can put it under the name instead of
                        wrapping the name onto two lines to fit it. */}
                    {a.people_who_may_charge > 1 && (
                      <span className="muted small cell--note">
                        {' '}
                        · {a.people_who_may_charge} may charge
                      </span>
                    )}
                  </td>
                  <td className="muted cell--aside" data-label="Category">
                    {a.category_name ?? '—'}
                    {a.membership_status && a.membership_status !== 'active' && (
                      <span className="small"> · {titleCase(a.membership_status)}</span>
                    )}
                  </td>
                  <td className="muted cell--aside" data-label="Member since">
                    {a.member_since ? date(a.member_since) : '—'}
                  </td>
                  {extra.map((c) => (
                    <td
                      key={c.code}
                      className="muted cell--aside"
                      data-label={c.label}
                    >
                      {a.roster_fields?.[c.code] ?? '—'}
                    </td>
                  ))}
                  <td data-label="Status">
                    <AccountStatusPill status={a.status} />
                  </td>
                  <td
                    // Nothing overdue is the ordinary case, and on a phone
                    // card an em-dash costs a whole line to say "no".
                    className={`num${Number(a.bucket_90_plus_minor) > 0 ? '' : ' cell--aside'}`}
                    data-label="Over 90 days"
                  >
                    {Number(a.bucket_90_plus_minor) > 0 ? (
                      <span className="neg">{money(a.bucket_90_plus_minor, currency)}</span>
                    ) : (
                      <span className="muted">—</span>
                    )}
                  </td>
                  <td className="num" data-label="Balance">
                    <Money minor={a.balance_minor} currency={currency} signed />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </>
        )}
      </Card>
    </>
  );
}

/* ------------------------------------------------------------------ */

interface AccountDetail {
  member_account_id: string;
  account_number: string;
  display_name: string;
  status: string;
  balance_minor: number;
  bucket_current_minor: number;
  bucket_30_minor: number;
  bucket_60_minor: number;
  bucket_90_plus_minor: number;
  autopay_enabled: boolean;
  credit_limit_minor: number | null;
  minimum: {
    period_starts_on: string;
    period_ends_on: string;
    required_minor: number;
    qualifying_spend_minor: number;
    remaining_minor: number;
  } | null;
}

interface TxnLine {
  line_number: number;
  description: string;
  quantity: string;
  unit_amount_minor: number;
  extended_minor: number;
  tax_minor: number;
}

interface Txn {
  id: string;
  txn_type: string;
  amount_minor: number;
  tax_minor: number;
  gratuity_minor: number;
  accounting_date: string;
  captured_at: string;
  /** When the ledger recorded it, as distinct from when it was ordered. */
  posted_at: string;
  charged_by_person_id: string | null;
  charged_by_name: string | null;
  description: string | null;
  source_module: string;
  outlet_name: string | null;
  reverses_txn_id: string | null;
  lines: TxnLine[];
}

export function MemberDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { currency } = useSession();
  const [expanded, setExpanded] = useState<string | null>(null);
  /**
   * Opened from the Autopay tile.
   *
   * "Maker member cards clickable, balance, over 90 days, autopay
   * (should pull up payment method CC entry)" — so the tile is the way
   * in rather than a link somewhere else on the page. Balance and
   * over-90 scroll to the activity and the aging they are counting;
   * autopay opens the cards, which is the thing you would go looking
   * for after reading it.
   */
  const [showCards, setShowCards] = useState(false);

  const account = useApi<AccountDetail>(id ? `/v1/member-accounts/${id}` : null);
  const txns = useApi<{ transactions: Txn[] }>(
    id ? `/v1/member-accounts/${id}/transactions?limit=100` : null,
  );

  if (account.loading) return <Loading rows={6} />;
  if (account.error) return <ErrorBanner error={account.error} />;
  const a = account.data;
  if (!a) return null;

  return (
    <>
      <div className="row" style={{ marginBottom: 16 }}>
        <div>
          <h1>{a.display_name}</h1>
          <div className="muted small mono">Account {a.account_number}</div>
        </div>
        <AccountStatusPill status={a.status} />
        <div className="spacer" />
        <Link to={`/charges/new?account=${a.member_account_id}`}>
          <button className="gold">Post a charge</button>
        </Link>
      </div>

      {/* Who may belong, and on what terms. Shown on the record it is
          about rather than filed under Settings. */}
      <BylawNotices surface="member.record" />

      <MembershipPanel accountId={a.member_account_id} />

      <div className="tiles">
        {/* Each of these is a question with an answer further down the
            page, and until now none of them said so. */}
        <Tile
          label="Balance"
          value={money(a.balance_minor, currency)}
          note="what is on the account"
          onClick={() =>
            document.getElementById('activity')?.scrollIntoView({ behavior: 'smooth' })
          }
        />
        <Tile
          label="Over 90 days"
          value={money(a.bucket_90_plus_minor, currency)}
          tone={Number(a.bucket_90_plus_minor) > 0 ? 'critical' : 'good'}
          note={Number(a.bucket_90_plus_minor) > 0 ? 'past due' : 'clear'}
          onClick={() =>
            document.getElementById('aging')?.scrollIntoView({ behavior: 'smooth' })
          }
        />
        <Tile
          label="Autopay"
          value={a.autopay_enabled ? 'On' : 'Off'}
          note={
            a.autopay_enabled
              ? 'charged automatically'
              : a.credit_limit_minor
                ? `Limit ${money(a.credit_limit_minor, currency)}`
                : 'settled by check or at the desk'
          }
          tone={a.autopay_enabled ? 'good' : 'neutral'}
          onClick={() => setShowCards((open) => !open)}
        />
      </div>

      {showCards && (
        <PaymentMethods
          accountId={a.member_account_id}
          autopayEnabled={a.autopay_enabled}
          onChanged={() => account.reload()}
        />
      )}

      <div className="grid-2" id="aging">
        <Card title="Receivables by Age">
          <AgingBar
            currency={currency}
            buckets={[
              { label: 'Current', value: a.bucket_current_minor, tone: 'good' },
              { label: '30 days', value: a.bucket_30_minor, tone: 'warning' },
              { label: '60 days', value: a.bucket_60_minor, tone: 'serious' },
              { label: '90+ days', value: a.bucket_90_plus_minor, tone: 'critical' },
            ]}
          />
        </Card>

        {/*
          Minimum progress is shown to staff for the same reason it is shown
          to members: clubs report that seeing the gap is what drives spend in
          the last weeks of a period. Hiding it until the forfeiture appears
          on a statement is how a club generates complaints.
        */}
        <Card title="Food &amp; Beverage Minimum">
          {!a.minimum ? (
            <Empty>No minimum period is open for this account.</Empty>
          ) : (
            <>
              <div className="row" style={{ marginBottom: 10 }}>
                <div>
                  <div className="muted small">
                    {date(a.minimum.period_starts_on)} – {date(a.minimum.period_ends_on)}
                  </div>
                  <div style={{ fontSize: 19, fontWeight: 650, marginTop: 2 }}>
                    {money(a.minimum.qualifying_spend_minor, currency)}{' '}
                    <span className="muted" style={{ fontWeight: 400, fontSize: 14 }}>
                      of {money(a.minimum.required_minor, currency)}
                    </span>
                  </div>
                </div>
                <div className="spacer" />
                <div style={{ textAlign: 'right' }}>
                  <div className="muted small">Remaining</div>
                  <div style={{ fontWeight: 650 }}>{money(a.minimum.remaining_minor, currency)}</div>
                </div>
              </div>
              <Meter
                value={Number(a.minimum.qualifying_spend_minor)}
                target={Number(a.minimum.required_minor)}
              />
              <div className="hint">
                Qualifying spend is measured net of tax and service charge.
              </div>
            </>
          )}
        </Card>
      </div>

      <UsageReport accountId={a.member_account_id} />

      <Card title="Activity" flush id="activity">
        {txns.loading ? (
          <div style={{ padding: 16 }}>
            <Loading />
          </div>
        ) : !txns.data || txns.data.transactions.length === 0 ? (
          <Empty>No activity on this account yet.</Empty>
        ) : (
          <table className="data">
            <thead>
              <tr>
                <th>Date</th>
                <th>Description</th>
                <th>Where</th>
                <th className="num">Tax</th>
                <th className="num">Amount</th>
              </tr>
            </thead>
            <tbody>
              {txns.data.transactions.map((t) => (
                <Fragment key={t.id}>
                  <tr>
                    <td
                      className="muted"
                      // The posting time, on the date. It has always been
                      // on the row and never been shown.
                      title={t.posted_at ? `Posted ${dateTime(t.posted_at)}` : undefined}
                    >
                      {date(t.accounting_date)}
                    </td>
                    <td>
                      {/* The description IS the way in, which is what was
                          asked for — "clicking description would also
                          share receipt". A button rather than a row
                          handler so it is reachable by keyboard and
                          announced as pressable, and so the row's other
                          cells stay selectable text. */}
                      <button
                        className="linkish"
                        aria-expanded={expanded === t.id}
                        onClick={() => setExpanded(expanded === t.id ? null : t.id)}
                      >
                        {t.description ?? titleCase(t.txn_type)}
                      </button>
                      {t.reverses_txn_id && <span className="muted small"> · reversal</span>}
                      {t.lines.length > 0 && (
                        <span className="muted small"> · {t.lines.length} items</span>
                      )}
                      {t.charged_by_name && (
                        <div className="muted small">{t.charged_by_name}</div>
                      )}
                    </td>
                    <td className="muted">{t.outlet_name ?? titleCase(t.source_module)}</td>
                    <td className="num muted">
                      {Number(t.tax_minor) ? money(t.tax_minor, currency) : '—'}
                    </td>
                    <td className="num">
                      <Money minor={t.amount_minor} currency={currency} signed />
                    </td>
                  </tr>
                  {/* The whole receipt, not a repeat of the lines.
                      It used to expand to the line items and a sentence
                      about the times, which answered "what was on it" and
                      neither of the other two questions a member rings up
                      with: who signed for it, and exactly when. */}
                  {expanded === t.id && (
                    <tr>
                      <td colSpan={5} style={{ background: 'var(--surface-sunk)' }}>
                        <Receipt
                          accountId={a.member_account_id}
                          txnId={t.id}
                          onClose={() => setExpanded(null)}
                        />
                      </td>
                    </tr>
                  )}
                </Fragment>
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </>
  );
}

/* ------------------------------------------------------------------ */

/**
 * Who this membership actually is.
 *
 * A club's roster is people, and an account screen that shows only a
 * balance is the reason staff keep a parallel spreadsheet of who is
 * allowed to sign for what. The panel is deliberately above the money:
 * the first question at the front desk is "can this person charge",
 * not "what do they owe".
 */

interface MembershipRow {
  id: string;
  membership_number: string;
  status: string;
  joined_on: string;
  resigned_on: string | null;
  category_code: string;
  category_name: string;
}

interface VehicleRow {
  id: string;
  plate: string;
  plate_region: string | null;
  make: string | null;
  model: string | null;
  color: string | null;
  is_primary: boolean;
}

interface PersonRow {
  id: string;
  membership_person_id: string;
  membership_id: string;
  first_name: string;
  last_name: string;
  preferred_name: string | null;
  salutation: string | null;
  email: string | null;
  phone: string | null;
  role: string;
  may_charge: boolean;
  starts_on: string;
  ends_on: string | null;
  date_of_birth: string | null;
  /**
   * Rendered by the database: "Mr. Shane McCarren (13)". The age is in
   * it only when the club would call this person a minor, and the club
   * says what that means. Computed on read, never stored — an age that
   * is stale in the direction of "old enough" is the expensive one.
   */
  label: string;
  age: number | null;
  is_minor: boolean;
  next_birthday: string | null;
  /** Whether the club may print the day and month (0192). Never the year. */
  publish_birthday: boolean;
  publish_birthday_set_at: string | null;
  photo_uri: string | null;
  photo_updated_at: string | null;
  vehicles: VehicleRow[];
}

interface MemberRecord {
  account: {
    id: string;
    account_number: string;
    display_name: string;
    status: string;
    /** The family photograph (0036). The terminal shows it at the bar. */
    photo_uri: string | null;
  };
  member_since: string | null;
  suggested_display_name: string | null;
  alerts: { person_id: string; person: string | null; label: string; value: string }[];
  memberships: MembershipRow[];
  people: PersonRow[];
  billing_address: {
    lines: string[];
    locality: string;
    region: string | null;
    postal_code: string | null;
  } | null;
}

function MembershipPanel({ accountId }: { accountId: string }) {
  const { atLeast } = useSession();
  const canEdit = atLeast('manager');
  const record = useApi<MemberRecord>(`/v1/members/${accountId}`);
  const [adding, setAdding] = useState(false);
  const [openPerson, setOpenPerson] = useState<string | null>(null);

  const categories = useApi<{ categories: CategoryRow[] }>(
    canEdit ? '/v1/membership-categories' : null,
  );

  const changeCategory = useMutation(async (membershipId: string, code: string) =>
    api(`/v1/memberships/${membershipId}`, {
      method: 'PATCH',
      body: { membership_category: code },
    }),
  );

  const rename = useMutation(async (display_name: string) =>
    api(`/v1/members/${accountId}`, { method: 'PATCH', body: { display_name } }),
  );

  const setJoined = useMutation(async (membershipId: string, joined_on: string) =>
    api(`/v1/memberships/${membershipId}`, { method: 'PATCH', body: { joined_on } }),
  );

  const endMembership = useMutation(
    async (membershipId: string, status: string, resigned_on: string) =>
      api(`/v1/memberships/${membershipId}`, {
        method: 'PATCH',
        body: { status, resigned_on },
      }),
  );

  const reinstate = useMutation(async (membershipId: string) =>
    api(`/v1/memberships/${membershipId}`, {
      method: 'PATCH',
      body: { status: 'active', resigned_on: null },
    }),
  );

  const setCharging = useMutation(async (membershipPersonId: string, may: boolean) =>
    api(`/v1/membership-people/${membershipPersonId}`, {
      method: 'PATCH',
      body: { may_charge: may },
    }),
  );

  if (record.loading) return <Loading rows={3} />;
  const r = record.data;
  if (!r) return <ErrorBanner error={record.error} />;

  const active = r.memberships.find((m) => m.status === 'active') ?? r.memberships[0];

  return (
    <Card
      title="Membership"
      actions={
        canEdit &&
        active && (
          <button className="secondary small" onClick={() => setAdding(true)}>
            + Add someone
          </button>
        )
      }
    >
      <ErrorBanner
        error={
          changeCategory.error ??
          setCharging.error ??
          setJoined.error ??
          endMembership.error ??
          reinstate.error
        }
      />

      {r.alerts.length > 0 && (
        <div
          style={{
            border: '1px solid var(--warning, #b45309)',
            borderRadius: 6,
            padding: '8px 12px',
            marginBottom: 14,
          }}
        >
          {r.alerts.map((a, i) => (
            <div key={i} className="small">
              <strong>⚠ {a.person}</strong>
              <span className="muted"> — {a.label}: </span>
              {a.value}
            </div>
          ))}
        </div>
      )}

      {/*
        The family photograph.
        0036 called it "the membership card and the directory", and the
        food and beverage terminal already reads it: a bartender about to
        put a drink on the Whitfield account sees the Whitfields. Which is
        the point Shaun made — "pic of family on main members (so if
        spouses book) the family pic shows for POS verification".
        It is on the ACCOUNT because a household has one; the portraits
        below it are on each PERSON because a spouse signing for lunch is
        somebody in particular.
      */}
      <PhotoPicker
        endpoint={`/v1/member-accounts/${accountId}/photo`}
        name={r.account.display_name}
        photoUri={r.account.photo_uri}
        label="The family photograph"
        hint="Shown wherever this account is charged — the bar, the pool gate, the pro shop — so whoever is serving can see who the household is."
        size={96}
        canEdit={canEdit}
        onChanged={() => record.reload()}
      />

      {r.suggested_display_name && r.suggested_display_name !== r.account.display_name && (
        <p className="muted small" style={{ marginTop: 0 }}>
          Statements are addressed to <strong>{r.account.display_name}</strong>.
          {canEdit && (
            <>
              {' '}From the member and spouse it would be{' '}
              <strong>{r.suggested_display_name}</strong>.{' '}
              <button
                className="secondary small"
                disabled={rename.busy}
                onClick={async () => {
                  const done = await rename.run(r.suggested_display_name!);
                  if (done) record.reload();
                }}
              >
                Use that
              </button>
            </>
          )}
        </p>
      )}

      <div className="row" style={{ gap: 32, marginBottom: 14 }}>
        <div>
          <div className="muted small">Member since</div>
          {/*
            Editable, because it is wrong on about a third of the records
            any club imports from whatever it ran before — and because it
            decides seniority, which decides tee times, lockers and who
            gets asked to join the board.
          */}
          {canEdit && active ? (
            <input
              type="date"
              value={(r.member_since ?? '').slice(0, 10)}
              disabled={setJoined.busy}
              onChange={async (e) => {
                if (!e.target.value) return;
                const done = await setJoined.run(active.id, e.target.value);
                if (done) record.reload();
              }}
              style={{ maxWidth: 160 }}
            />
          ) : (
            <strong>{r.member_since ? date(r.member_since) : '—'}</strong>
          )}
        </div>
        <div>
          <div className="muted small">Category</div>
          {canEdit && active ? (
            <select
              value={active.category_code}
              disabled={changeCategory.busy}
              onChange={async (e) => {
                const done = await changeCategory.run(active.id, e.target.value);
                if (done) record.reload();
              }}
              style={{ maxWidth: 220 }}
            >
              {(categories.data?.categories ?? []).map((c) => (
                <option key={c.id} value={c.code}>
                  {c.name}
                </option>
              ))}
            </select>
          ) : (
            <strong>{active?.category_name ?? '—'}</strong>
          )}
        </div>
        <div>
          <div className="muted small">Membership</div>
          <strong className="mono">{active?.membership_number ?? '—'}</strong>
          {active && active.status !== 'active' && (
            <span className="muted small">
              {' '}
              · {titleCase(active.status)}
              {active.resigned_on ? ` ${date(active.resigned_on)}` : ''}
            </span>
          )}
        </div>
        {r.billing_address && (
          <div>
            <div className="muted small">Statements go to</div>
            <span>
              {r.billing_address.lines.join(', ')}, {r.billing_address.locality}
              {r.billing_address.region ? `, ${r.billing_address.region}` : ''}{' '}
              {r.billing_address.postal_code ?? ''}
            </span>
          </div>
        )}
      </div>

      {canEdit && active && (
        <EndMembershipControl
          membership={active}
          busy={endMembership.busy || reinstate.busy}
          onEnd={async (status, on) => {
            const done = await endMembership.run(active.id, status, on);
            if (done) record.reload();
          }}
          onReinstate={async () => {
            const done = await reinstate.run(active.id);
            if (done) record.reload();
          }}
        />
      )}

      {/* Spouses, children, and the age a privilege ends at. Shown
          where the household is actually changed. */}
      {adding && <BylawNotices surface="member.family" />}

      {adding && active && (
        <AddPersonForm
          membershipId={active.id}
          defaultLastName={r.people[0]?.last_name ?? ''}
          onDone={() => {
            setAdding(false);
            record.reload();
          }}
          onCancel={() => setAdding(false)}
        />
      )}

      <table className="data">
        <thead>
          <tr>
            <th>Name</th>
            <th>Relationship</th>
            <th>Contact</th>
            <th>May charge</th>
            {canEdit && <th />}
          </tr>
        </thead>
        <tbody>
          {r.people.map((p) => {
            const ended = p.ends_on !== null && p.ends_on < new Date().toISOString().slice(0, 10);
            return (
              <Fragment key={p.membership_person_id}>
              <tr className={ended ? 'muted' : undefined}>
                <td>
                  <button
                    className="secondary small"
                    style={{ marginRight: 8 }}
                    aria-expanded={openPerson === p.id}
                    onClick={() => setOpenPerson(openPerson === p.id ? null : p.id)}
                  >
                    {openPerson === p.id ? '−' : '+'}
                  </button>
                  {p.label}
                  {ended && <span className="muted small"> · left {date(p.ends_on)}</span>}
                </td>
                <td className="muted">
                  {titleCase(p.role)}
                  {/*
                    Birthday and age together. A club needs the date to send
                    a card and the age to know when a junior stops being one,
                    and computing it in the reader's head is how a member
                    gets billed as an adult a year late.
                  */}
                  {p.date_of_birth && (
                    <div className="small">
                      {date(p.date_of_birth)}
                      {/* The age is already in the name for a minor. Printing
                          it twice reads as though the two might disagree. */}
                      {!p.is_minor && p.age !== null && ` · ${p.age}`}
                    </div>
                  )}
                </td>
                <td className="muted small">
                  {p.email ?? ''}
                  {p.email && p.phone ? ' · ' : ''}
                  {p.phone ?? ''}
                  {!p.email && !p.phone ? '—' : ''}
                  {p.vehicles.length > 0 && (
                    <div className="mono">{p.vehicles.map((v) => v.plate).join(', ')}</div>
                  )}
                </td>
                <td>{p.may_charge && !ended ? 'Yes' : <span className="muted">No</span>}</td>
                {canEdit && (
                  <td className="num">
                    <button
                      className="secondary small"
                      disabled={setCharging.busy || p.role === 'primary'}
                      title={
                        p.role === 'primary'
                          ? 'The primary member always may charge'
                          : undefined
                      }
                      onClick={async () => {
                        const done = await setCharging.run(
                          p.membership_person_id,
                          !p.may_charge,
                        );
                        if (done) record.reload();
                      }}
                    >
                      {p.may_charge ? 'Revoke charging' : 'Allow charging'}
                    </button>
                  </td>
                )}
              </tr>
              {openPerson === p.id && (
                <tr>
                  <td colSpan={canEdit ? 5 : 4} style={{ background: 'var(--surface-sunk)' }}>
                    <PersonDetail
                      personId={p.id}
                      person={p}
                      vehicles={p.vehicles}
                      onChanged={() => record.reload()}
                    />
                  </td>
                </tr>
              )}
              </Fragment>
            );
          })}
        </tbody>
      </table>
    </Card>
  );
}

function AddPersonForm({
  membershipId,
  defaultLastName,
  onDone,
  onCancel,
}: {
  membershipId: string;
  defaultLastName: string;
  onDone: () => void;
  onCancel: () => void;
}) {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState(defaultLastName);
  const [role, setRole] = useState<'spouse' | 'dependent' | 'authorized_user'>('spouse');
  const [mayCharge, setMayCharge] = useState(true);

  const add = useMutation(async () =>
    api(`/v1/memberships/${membershipId}/people`, {
      method: 'POST',
      body: {
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        role,
        may_charge: mayCharge,
      },
    }),
  );

  return (
    <div className="row" style={{ marginBottom: 14, alignItems: 'flex-end' }}>
      <ErrorBanner error={add.error} />
      <label>
        <span className="muted small">First name</span>
        <input value={firstName} onChange={(e) => setFirstName(e.target.value)} autoFocus />
      </label>
      <label>
        <span className="muted small">Last name</span>
        <input value={lastName} onChange={(e) => setLastName(e.target.value)} />
      </label>
      <label>
        <span className="muted small">Relationship</span>
        <select value={role} onChange={(e) => setRole(e.target.value as typeof role)}>
          <option value="spouse">Spouse</option>
          <option value="dependent">Dependent</option>
          <option value="authorized_user">Authorized user</option>
        </select>
      </label>
      <label className="muted small" style={{ display: 'flex', gap: 6, paddingBottom: 8 }}>
        <input
          type="checkbox"
          checked={mayCharge}
          onChange={(e) => setMayCharge(e.target.checked)}
        />
        May charge
      </label>
      <button
        disabled={add.busy || !firstName.trim() || !lastName.trim()}
        onClick={async () => {
          const done = await add.run();
          if (done) onDone();
        }}
      >
        {add.busy ? 'Adding…' : 'Add'}
      </button>
      <button className="secondary" onClick={onCancel}>
        Cancel
      </button>
    </div>
  );
}

/**
 * Ending a membership, and putting one back.
 *
 * Resigned and terminated are not the same event and a club must not be
 * made to pick one to mean both: a member who leaves owing nothing and a
 * member who is expelled get different letters, different reinstatement
 * rules, and in most clubs' bylaws different treatment of their equity.
 *
 * Both need a date, and the date is the point. A club that cannot say
 * who was a member on a given day cannot answer an assessment dispute, a
 * subpoena or a reinstatement request — which is why nothing here
 * deletes anything, and why the API refuses an ending with no date
 * rather than defaulting it to today.
 */
function EndMembershipControl({
  membership,
  busy,
  onEnd,
  onReinstate,
}: {
  membership: MembershipRow;
  busy: boolean;
  onEnd: (status: 'resigned' | 'terminated', on: string) => Promise<void>;
  onReinstate: () => Promise<void>;
}) {
  const [open, setOpen] = useState(false);
  const [kind, setKind] = useState<'resigned' | 'terminated'>('resigned');
  const [on, setOn] = useState(new Date().toISOString().slice(0, 10));

  const over = membership.status === 'resigned' || membership.status === 'terminated';

  if (over) {
    return (
      <p className="muted small" style={{ marginTop: 0 }}>
        {titleCase(membership.status)}
        {membership.resigned_on ? ` on ${date(membership.resigned_on)}` : ''}.{' '}
        <button className="secondary small" disabled={busy} onClick={onReinstate}>
          Reinstate
        </button>
      </p>
    );
  }

  if (!open) {
    return (
      <p style={{ marginTop: 0 }}>
        <button className="secondary small" onClick={() => setOpen(true)}>
          End this membership
        </button>
      </p>
    );
  }

  return (
    <>
      {/* What the club's own by-laws say about a resignation — when it
          takes effect, and what is owed until it does. Article IX at
          Rockford, which has no sections and could not have been pointed
          at if a notice had to hang on one. */}
      <BylawNotices surface="member.resign" />

    <div
      style={{
        border: '1px solid var(--border, #d8d3c9)',
        borderRadius: 6,
        padding: '10px 14px',
        marginBottom: 14,
        display: 'flex',
        gap: 12,
        alignItems: 'flex-end',
        flexWrap: 'wrap',
      }}
    >
      <label>
        <span className="muted small">What happened</span>
        <select value={kind} onChange={(e) => setKind(e.target.value as typeof kind)}>
          <option value="resigned">Resigned</option>
          <option value="terminated">Terminated</option>
        </select>
      </label>

      <label>
        <span className="muted small">Effective</span>
        <input type="date" value={on} onChange={(e) => setOn(e.target.value)} />
      </label>

      <button
        className="gold small"
        disabled={busy || !on}
        onClick={async () => {
          await onEnd(kind, on);
          setOpen(false);
        }}
      >
        {busy ? 'Saving…' : 'Record it'}
      </button>
      <button className="secondary small" onClick={() => setOpen(false)}>
        Cancel
      </button>

      <span className="muted small" style={{ flexBasis: '100%' }}>
        Nothing is deleted. The membership stays on the record with this date, so
        "who was a member in {new Date(on).getFullYear()}" keeps having an answer.
      </span>
    </div>
    </>
  );
}
